// CFT = (L × W × H) / 1728 × qty
export function calcCFT(l, w, h, qty = 1) {
  const L = parseFloat(l) || 0;
  const W = parseFloat(w) || 0;
  const H = parseFloat(h) || 0;
  const Q = parseInt(qty) || 1;
  if (!L || !W || !H) return 0;
  return (L * W * H / 1728) * Q;
}

// Volumetric weight: CFT × 10.4
export function calcVolWeight(cft) {
  return cft * 10.4;
}

// Chargeable weight = max(actual, volumetric)
export function calcChargeableWeight(actualLbs, cft) {
  return Math.max(parseFloat(actualLbs)||0, calcVolWeight(cft));
}

// CIF = (commercial value + freight USD total) × ROE
export function calcCIF(commercialValueUSD, totalFreightUSD, roe) {
  const cv = parseFloat(commercialValueUSD) || 0;
  const fr = parseFloat(totalFreightUSD) || 0;
  return (cv + fr) * (parseFloat(roe) || 6.77);
}

// Value-based brokerage fee from CIF tiers
export function calcValueBrokerage(cifValueTTD, tiers) {
  if (!tiers || !tiers.length) return 500;
  const sorted = [...tiers].sort((a, b) => a.cif_min - b.cif_min);
  for (const tier of sorted) {
    const maxOk = tier.cif_max == null || cifValueTTD <= tier.cif_max;
    if (cifValueTTD >= tier.cif_min && maxOk) {
      const fee = cifValueTTD * tier.rate;
      return Math.max(fee, tier.minimum_fee || 0);
    }
  }
  return cifValueTTD * 0.005;
}

// Transport quote calculation
export function calcTransport(params, zones) {
  const {
    distanceKm, requiresSecondMan, afterHours, tailLift,
    waitingMinutes, zoneId,
  } = params;

  const zone = zones.find(z => z.zone === parseInt(zoneId));
  if (!zone || (!zone.base_rate_ttd && !zone.per_km_ttd)) {
    return { total: 0, lines: [], error: 'Zone rates not configured yet. Set rates in Transport Rates admin.' };
  }

  const p = {}; // transport parameters come from DB
  const lines = [];
  let total = 0;

  const base = zone.base_rate_ttd || 0;
  if (base > 0) {
    lines.push({ description: `Base rate — Zone ${zone.zone} (${zone.example_areas})`, ttd: base });
    total += base;
  }

  const extraKm = Math.max(0, (parseFloat(distanceKm)||0) - (zone.distance_max||0));
  if (extraKm > 0 && zone.per_km_ttd > 0) {
    const extraCharge = extraKm * zone.per_km_ttd;
    lines.push({ description: `Extra distance (${extraKm.toFixed(1)} km × TT$${zone.per_km_ttd}/km)`, ttd: extraCharge });
    total += extraCharge;
  }

  return { total, lines, zone };
}

export function buildQuoteLines(fields, rates, settings, brokerageRates, cifTiers) {
  const {
    service, origin,
    actualWeightLbs, qty, length, width, height,
    commercialValue, roe,
    includeBrokerage, includeTransport,
    caricom, sedEei, insurance, inlandPickup,
    freightCollectFee,
  } = fields;

  const ROE = parseFloat(roe || settings?.default_roe || 6.77);
  const VAT_RATE = parseFloat(settings?.vat_rate || 0.125);

  const cft = calcCFT(length, width, height, qty);
  const volWeight = calcVolWeight(cft);
  const chargeableWeight = calcChargeableWeight(actualWeightLbs, cft);

  const serviceRates = rates.filter(r => r.service === service && r.origin === origin);
  const freightRates = serviceRates.filter(r => r.category === 'freight');
  const localRates = serviceRates.filter(r => r.category === 'local');

  const lines = [];
  let totalUSD = 0;
  let totalTTD = 0;
  let localTTDBase = 0; // for VAT calculation

  // ── FCL overrides ──────────────────────────────────────────────────────────
  if (service === 'fcl20' || service === 'fcl40' || service === 'fcl45') {
    const fclRates = {
      fcl20: { label: "FCL 20' DV", usd: 2800, pss: 250 },
      fcl40: { label: "FCL 40' HC", usd: 4200, pss: 500 },
      fcl45: { label: "FCL 45' HC", usd: 4550, pss: 563 },
    };
    const fcl = fclRates[service];
    lines.push({ description: `${fcl.label} Ocean Freight`, usd: fcl.usd, ttd: fcl.usd * ROE });
    totalUSD += fcl.usd;
    totalTTD += fcl.usd * ROE;

    if (fcl.pss > 0) {
      lines.push({ description: 'Peak Season Surcharge (PSS)', usd: fcl.pss, ttd: fcl.pss * ROE });
      totalUSD += fcl.pss;
      totalTTD += fcl.pss * ROE;
    }

    const destCharges = [
      { description: 'Destination Handling Charge (FCL)', ttd: 2535 },
      { description: 'Local Admin Charge (LAC)', ttd: 487.5 },
      { description: 'VAT', ttd: 60.9375 },
      { description: 'Local Handling', ttd: 350 },
    ];
    destCharges.forEach(d => {
      lines.push({ description: d.description, usd: null, ttd: d.ttd });
      totalTTD += d.ttd;
    });

    return { lines, totalUSD, totalTTD, cft, volWeight, chargeableWeight };
  }

  // ── Ocean / Air ────────────────────────────────────────────────────────────
  freightRates.forEach(r => {
    // Skip zero-value optional charges unless toggled on
    if (r.name === 'Caricom' && !caricom) return;
    if (r.name === 'SED/EEI' && !sedEei) return;
    if (r.name === 'Insurance' && !insurance) return;
    if (r.name === 'Freight Collect Fee (5%)') return; // handled separately

    let usd = r.value;
    // Per-CFT charges
    if (service === 'ocean' && r.name === 'Bunker') usd = r.value * cft;
    // Air freight: rate per 100 lbs chargeable weight
    if (service === 'air' && r.name === 'Air Freight Charges') usd = (r.value * chargeableWeight) / 100;
    // Air Peak Season Surcharge: also per 100 lbs
    if (service === 'air' && r.name === 'Peak Season Surcharge' && r.value > 0) usd = (r.value * chargeableWeight) / 100;

    // Insurance: 0.1 cent per dollar of CIF value TTD (calculated after we know totalUSD)
    if (r.name === 'Insurance') {
      // Will be calculated after freight totals are known — skip here
      return;
    }
    // Show Caricom/SED even if zero (they were toggled on by user)
    if (usd === 0 && r.value === 0) {
      if (r.name === 'Caricom' || r.name === 'SED/EEI') {
        lines.push({ description: r.name, usd: 0, ttd: 0 });
      }
      return;
    }

    const ttd = usd * ROE;
    lines.push({ description: r.name, usd, ttd });
    totalUSD += usd;
    totalTTD += ttd;
  });

  // Inland Pick-up (optional, USD)
  if (inlandPickup && parseFloat(inlandPickup) > 0) {
    const usd = parseFloat(inlandPickup);
    lines.push({ description: 'Inland Pick-up', usd, ttd: usd * ROE });
    totalUSD += usd;
    totalTTD += usd * ROE;
  }

  // Insurance: 0.001 × CIF value TTD (0.1 cent per dollar of CIF)
  if (insurance) {
    const cifForInsurance = calcCIF(commercialValue, totalUSD, ROE);
    const insuranceUSD = cifForInsurance * 0.001 / ROE; // convert back to USD for display
    const insuranceTTD = cifForInsurance * 0.001;
    if (insuranceTTD > 0) {
      lines.push({ description: 'Insurance (0.1% of CIF)', usd: insuranceUSD, ttd: insuranceTTD });
      totalUSD += insuranceUSD;
      totalTTD += insuranceTTD;
    }
  }

  // Local TTD charges
  localRates.forEach(r => {
    if (r.name === 'Freight Collect Fee (5%)') return; // handled separately

    let ttd = r.value;
    // Per-CFT local charges
    if (['Security Surcharge', 'Unstuffing', 'Local Inland Transport', 'DTHC/Other'].includes(r.name)) {
      ttd = r.value * cft;
    }
    if (ttd === 0) return;

    lines.push({ description: r.name, usd: null, ttd });
    totalTTD += ttd;
    localTTDBase += ttd;
  });

  // Freight collect fee 5% (air only) — 5% of total local TTD charges
  if (service === 'air' && freightCollectFee) {
    const feeBase = localTTDBase;
    const fee = feeBase * 0.05;
    if (fee > 0) {
      lines.push({ description: 'Freight Collect Fee (5%)', usd: null, ttd: fee });
      totalTTD += fee;
      localTTDBase += fee;
    }
  }

  // VAT on local TTD charges
  const vat = localTTDBase * VAT_RATE;
  if (vat > 0) {
    lines.push({ description: `VAT (${(VAT_RATE * 100).toFixed(1)}%)`, usd: null, ttd: vat });
    totalTTD += vat;
  }

  // CIF value for brokerage
  const cifValueTTD = calcCIF(commercialValue, totalUSD, ROE);

  // Brokerage section
  let brokerageLines = [];
  let brokerageTotal = 0;
  if (includeBrokerage && brokerageRates && brokerageRates.length) {
    const svc = (service === 'air') ? 'air' : 'ocean';
    brokerageRates.forEach(br => {
      let amount = svc === 'air' ? br.rate_air : br.rate_ocean;
      if (br.name === 'Value Based Brokerage Fee') {
        amount = calcValueBrokerage(cifValueTTD, cifTiers);
      }
      if (amount > 0) {
        brokerageLines.push({ description: br.name, ttd: amount });
        brokerageTotal += amount;
      }
    });
  }

  return {
    lines, totalUSD, totalTTD,
    cft, volWeight, chargeableWeight,
    cifValueTTD,
    brokerageLines, brokerageTotal,
  };
}
